/**
 * Email inbound — suspended queue API (T-012 · docs/10-design/06 §2.3)
 * cause ∈ `bounce|auto_reply|loop|spam|no_route|unauthenticated_update|notify_token_sender_mismatch|ticket_closed` (W1-CONTRACTS §6 · W3 #34 · E-05 R2/D-039)
 */
import { z } from "zod";

export const SuspendedCause = z.enum([
  "bounce",
  "auto_reply",
  "loop",
  "spam",
  "no_route",
  "unauthenticated_update",
  // [W8 NP-1a · W3 #34] notify+ token จากอีเมลที่ไม่ใช่ของ user นั้น (emit โดย pipeline ตั้งแต่ T-031 R5)
  "notify_token_sender_mismatch",
  "ticket_closed",
]);
export type SuspendedCause = z.infer<typeof SuspendedCause>;

export const SuspendedEmail = z.object({
  id: z.uuid(),
  cause: SuspendedCause,
  /** [E-05 R2] ticket_closed = ลูกค้าตอบ notify+ token บนการ์ดที่ปิดแล้ว (แขวนไว้ให้ inspect/recover — D-039) */
  cause_detail: z.string().nullable(),
  from_email: z.string().nullable(),
  subject: z.string().nullable(),
  snippet: z.string().nullable(),
  mailbox_id: z.uuid().nullable(),
  created_at: z.iso.datetime(),
  resolved_at: z.iso.datetime().nullable(),
});
export type SuspendedEmail = z.infer<typeof SuspendedEmail>;

export const SuspendedEmailDetail = SuspendedEmail.extend({
  inbound_message_id: z.uuid(),
  raw_r2_key: z.string().nullable(),
});
export type SuspendedEmailDetail = z.infer<typeof SuspendedEmailDetail>;

export const SuspendedListQuery = z.object({
  cause: SuspendedCause.optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type SuspendedListQuery = z.infer<typeof SuspendedListQuery>;

export const SuspendedList = z.object({
  items: z.array(SuspendedEmail),
  next_cursor: z.string().nullable(),
});
export type SuspendedList = z.infer<typeof SuspendedList>;

export const RecoverSuspended = z
  .object({
    action: z.enum(["create_ticket", "attach"]),
    /** required-ish for create_ticket: falls back to the routed mailbox's service */
    service_code: z.string().regex(/^[A-Z0-9]{2,4}$/).optional(),
    team_code: z.string().max(64).optional(),
    /** required for action=attach — ticket public id with check digits */
    ticket_id: z.string().regex(/^[A-Z0-9]{2,4}-\d{11}$/).optional(),
  })
  .refine((v) => v.action !== "attach" || typeof v.ticket_id === "string", { message: "attach ต้องระบุ ticket_id" });
export type RecoverSuspended = z.infer<typeof RecoverSuspended>;

export const RecoverSuspendedResult = z.object({
  id: z.uuid(),
  resolution: z.string(),
  ticket_id: z.string().nullable(),
  message_id: z.string().nullable(),
});
export type RecoverSuspendedResult = z.infer<typeof RecoverSuspendedResult>;

/** Admin repair surface for failures and queued/processing jobs stranded in a DLQ. */
export const InboundRecoveryListQuery = z.object({
  status: z.enum(["queued", "processing", "failed"]).optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const InboundRecoveryList = z.object({
  items: z.array(z.object({
    id: z.uuid(), status: z.enum(["queued", "processing", "failed"]),
    error: z.string().nullable(), received_at: z.iso.datetime(), processed_at: z.iso.datetime().nullable(),
    decision: z.record(z.string(), z.unknown()).nullable(),
  })),
  next_cursor: z.string().nullable(),
});
export const InboundRequeueResult = z.object({ id: z.uuid(), status: z.literal("queued"), published: z.boolean() });
